import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dropdown,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import CodeIcon from "@mui/icons-material/Code";
import CustomModal from "components/common/CustomModal";
import TaskEditorTextField from "./TaskEditorTextField";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import {
  buildTaskEditorBlockGroups,
  convertTaskEditorImageFileToDataUrl,
  getTaskEditorBlockDisplayLabels,
  getTaskEditorBranchRunSpan,
  getTaskEditorBranchSegmentBlocks,
  getTaskEditorComponentTypeLabel,
  getTaskEditorDescriptionTypeLabel,
  getTaskEditorGroupReorderTarget,
  getTaskEditorQuizOptionLabel,
  getTaskEditorReferenceTypeLabel,
  isSameTaskEditorBlockDropPreview,
  isTaskEditorLocalImageDataUrl,
  isTaskEditorUndoShortcut,
  reorderTaskEditorBlockGroup,
  TASK_EDITOR_ACTION_COMPONENT_TYPES,
  TASK_EDITOR_DESCRIPTION_TYPES,
  TASK_EDITOR_REFERENCE_TYPES,
  TaskEditorActionComponent,
  TaskEditorActionComponentType,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorBranchScope,
  TaskEditorBranchSegment,
  TaskEditorDeleteTarget,
  TaskEditorDescription,
  TaskEditorDescriptionType,
  TaskEditorReferenceType,
  TaskEditorState,
} from "./utils/taskEditorUtils";
import { useTaskEditorBlockDrag } from "./utils/useTaskEditorBlockDrag";
import { useTaskEditorDragAutoScroll } from "./utils/useTaskEditorDragAutoScroll";
import { useTaskEditorUndoHistory } from "./utils/useTaskEditorUndoHistory";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import TaskEditorBranchFlow from "./TaskEditorBranchFlow";
import { JsonCodeBox, orderedStringify } from "./jsonCodeView";
import {
  applyActionComponentTypeChange,
  applyAddAction,
  applyAddBlock,
  applyAddButtonOption,
  applyAddLabelPair,
  applyAddTextInputField,
  applyAudioFieldChange,
  applyBlockTypeChange,
  applyBranchSegmentBlocksChange,
  applyButtonDefaultAllowMultipleChange,
  applyButtonOptionTextChange,
  applyButtonQuizOptionCorrectToggle,
  applyDescriptionContentChange,
  applyDescriptionImageUrlChange,
  applyDescriptionTypeChange,
  applyLabelPairChange,
  applyReferencePresetTextChange,
  applyReferenceTypeChange,
  applyRemoveActionComponent,
  applyRemoveBlock,
  applyRemoveButtonOption,
  applyRemoveLabelPair,
  applyRemoveTextInputField,
  applyTextInputFieldChange,
  applyTextInputFieldOptionalToggle,
} from "./utils/taskEditorMutations";

// ─── Sx constants ─────────────────────────────────────────────────────────

const panelScrollbarSx = {
  minHeight: 0,
  overflowY: "auto",
  ...taskEditorScrollbarSx,
} as const;

const taskEditorBlockCardSx = {
  position: "relative",
  p: "10px",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorComponentCardSx = {
  px: 0,
  py: 0.5,
  border: "none",
  borderRadius: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
  transition: "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
} as const;

const taskEditorDashedDividerSx = {
  borderTop: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  height: 0,
  my: 2.5,
} as const;

const taskEditorBlockDropZoneSx = {
  position: "relative",
  height: "16px",
  mx: "8px",
  borderRadius: "12px",
  transition:
    "height 0.2s cubic-bezier(0.2, 0, 0, 1), opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorAddBlockButtonWrapSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  py: 1,
} as const;

const taskEditorAddBlockButtonSx = {
  minWidth: "34px",
  width: "34px",
  height: "34px",
  borderRadius: "999px",
  border: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  color: theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Strong,
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

// + 블록 추가 메뉴(헤더/바디) 전용 — 공용 Select listbox 보다 조금 더 크게
// (너비·항목 높이·글씨). 다른 Select 드롭다운에는 영향 없음.
const taskEditorAddBlockMenuSx = {
  p: 0.5,
  minWidth: 168,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  borderRadius: "12px",
  boxShadow:
    "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
  "--List-gap": "4px",
  "--ListItem-radius": "10px",
  "& .MuiMenuItem-root": {
    minHeight: "42px",
    px: 1.5,
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    color: theme.vars.palette.Content.Medium,
  },
  "& .MuiMenuItem-root:hover": {
    backgroundColor: theme.vars.palette.Background.Level1,
  },
} as const;

const taskEditorDeleteTargetLabelMap = {
  block: "블록",
  action: "컴포넌트",
} as const;

const taskEditorSelectSx = {
  minHeight: "28px",
  px: 1,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "none",
  color: theme.vars.palette.Content.Strong,
  fontSize: "12px",
  fontWeight: 600,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "--Select-placeholderOpacity": 1,
  "--Select-decoratorColor": theme.vars.palette.Content.Weak,
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Surface.Forward,
    transform: "translateY(-1px)",
  },
  "&:focus-within": {
    borderColor: theme.vars.palette.primary.soft,
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.vars.palette.Background.Level1,
    color: theme.vars.palette.Content.Weak,
    boxShadow: "none",
  },
} as const;

const taskEditorSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSubtypeSelectSx = {
  minHeight: "28px",
  px: 1,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: "transparent",
  boxShadow: "none",
  color: theme.vars.palette.Content.Medium,
  fontSize: "12px",
  fontWeight: 600,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Background.Level1,
  },
} as const;

const taskEditorSubtypeSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSelectListboxSx = {
  p: 0.25,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  borderRadius: "10px",
  boxShadow:
    "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
  "--List-gap": "2px",
  "--ListItem-radius": "8px",
  "& .MuiOption-root, & .MuiMenuItem-root": {
    minHeight: "32px",
    px: 1,
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: theme.vars.palette.Content.Medium,
  },
  "& .MuiOption-root:hover, & .MuiMenuItem-root:hover": {
    backgroundColor: theme.vars.palette.Background.Level1,
  },
  "& .MuiOption-root[aria-selected='true']": {
    backgroundColor: theme.vars.palette.Background.Level2,
    color: theme.vars.palette.Content.Strong,
  },
} as const;

const taskEditorCompactFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.3,
} as const;

const taskEditorButtonGroupEditorSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
} as const;

const taskEditorCompactFieldSx = {
  minWidth: 0,
} as const;

const taskEditorButtonGroupOptionListSx = {
  display: "grid",
  gap: 0.75,
  minWidth: 0,
} as const;

const taskEditorButtonGroupGuideTriggerSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const taskEditorButtonGroupGuideIconSx = {
  minWidth: "20px",
  minHeight: "20px",
  width: "20px",
  height: "20px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  color: theme.vars.palette.Content.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  "&:hover": { backgroundColor: theme.vars.palette.Background.Level2 },
} as const;

const taskEditorButtonGroupGuideTooltipSx = {
  whiteSpace: "pre-line",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "#FFFFFF",
  maxWidth: "300px",
} as const;

const taskEditorButtonGroupGuideTooltipSlotSx = {
  backgroundColor: "rgba(15, 23, 42, 0.96)",
  color: "#FFFFFF",
  boxShadow: "0 10px 28px rgba(2, 6, 23, 0.45)",
  borderRadius: "10px",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  px: 1.25,
  py: 1,
  maxWidth: "340px",
} as const;

const taskEditorButtonGroupGuideTooltipArrowSx = {
  "&::before": {
    borderTopColor: "rgba(15, 23, 42, 0.96)",
    borderRightColor: "rgba(15, 23, 42, 0.96)",
    boxShadow: "none",
  },
} as const;

const TASK_EDITOR_BUTTON_BRANCH_GUIDE_TEXT = [
  "각 옵션은 자기만의 내부 블록을 가집니다.",
  '옵션의 "내부 블록 편집" 으로 들어가 그 옵션을 선택했을 때 보여줄 블록들을 추가/편집하세요.',
  "내부 블록이 끝나면 분기 다음 블록으로 자동 합류합니다.",
  "내부 블록이 없는 옵션은 선택 즉시 다음 블록으로 진행합니다.",
].join("\n");

const taskEditorButtonGroupOptionRowSx = {
  display: "grid",
  gap: 0.5,
  minWidth: 0,
} as const;

// 루트 목록에서 분기 카드 아래의 세그먼트(내부 블록) 영역 — 플로우차트식
// 가로 컬럼. 커넥터/컬럼 sx 는 utils/taskEditorBranchFlowSx 공용.
const taskEditorSegmentGroupSx = {
  mt: 0,
} as const;

// 옵션 1개의 "블록 모음" 패널 — 헤더(옵션 라벨 + 편집) 아래에 미니 블록
// 카드들이 쌓인다.
const taskEditorSegmentPanelSx = {
  minWidth: 0,
  borderRadius: "10px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  px: 1,
  py: 0.75,
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
} as const;

// 세그먼트 블록 1개의 미니 카드 — 본 블록 카드의 축소판 (드릴다운 진입 타깃).
const taskEditorSegmentRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.25,
  py: 0.75,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
  cursor: "pointer",
  textAlign: "left" as const,
  width: "100%",
  transition: "border-color 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

const taskEditorButtonGroupOptionChipSx = {
  // chip 이 행 너비를 가득 채워야 안쪽 옵션 입력칸도 full width 가 된다.
  // Joy Chip root 는 기본 maxWidth:"max-content" (flex 안에서 늘어남 방지용) 라
  // width:"100%" 만으로는 content 폭에 묶인다 → maxWidth 를 풀어 늘린다.
  width: "100%",
  maxWidth: "none",
  minHeight: "28px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  px: 0.75,
  py: 0.25,
} as const;

const taskEditorButtonGroupOptionInputSx = {
  minWidth: 0,
  // 고정폭 대신 행 내 잔여 공간을 가득 채워 긴 옵션 텍스트가 "…" 로 잘리지 않게 한다.
  flex: 1,
  "--Input-radius": "999px",
  "--Input-minHeight": "24px",
  "--Input-paddingInline": "8px",
  "--Input-decoratorChildHeight": "16px",
  "& .MuiInput-input": { fontSize: "12px", fontWeight: 600 },
} as const;

// 편집 폼 Input(single-line) — Agent 채팅(agent/chatInput.tsx) 의 focus 하이라이트와 동일한 느낌:
// MUI Joy 기본 focus ring(~2px) 을 1px 로 줄이고 색을 Border.Highlight 로 전환.
// alignItems center 로 단일 라인 텍스트 세로 중앙 정렬을 보장한다.
const taskEditorTextFieldSx = {
  alignItems: "center",
  "--Input-focusedThickness": "1px",
  "--Input-focusedHighlight": theme.vars.palette.Border.Highlight,
} as const;

// 편집 폼 Textarea(multiline) — Input 과 달리 column flex 이므로 alignItems center 미포함.
// Textarea 전용 CSS 변수로 동일한 1px / Border.Highlight focus 링을 적용한다.
const taskEditorTextareaFieldSx = {
  "--Textarea-focusedThickness": "1px",
  "--Textarea-focusedHighlight": theme.vars.palette.Border.Highlight,
} as const;

const taskEditorInlineAddButtonSx = {
  fontSize: "12px",
  fontWeight: 600,
  minHeight: "28px",
  borderRadius: "999px",
  px: 1.25,
  // grid item 이라 기본적으로 행 너비를 가득 채우는데, 일반 옵션 chip 처럼
  // 자기 컨텐츠 폭만 차지하도록 좌측 정렬.
  justifySelf: "start",
} as const;

const taskEditorImagePreviewSx = {
  width: "100%",
  maxHeight: "160px",
  objectFit: "contain",
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
} as const;

// 세그먼트 컴팩트 행의 본문 요약 — description 텍스트(content).
function getTaskEditorBlockSummaryText(block: TaskEditorBlock): string {
  const text = block.description.content.trim();
  return text || "(내용 없음)";
}

// ─── Props & Component ─────────────────────────────────────────────────────

export interface TaskEditorWorkspaceProps {
  editorState: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
  // 분기 드릴다운 스코프 — 부모(패널) 소유. 워크스페이스가 편집 탭을 벗어나
  // 언마운트돼도 유지되고, 미리보기 탭이 같은 스코프로 분기 내 미리보기를 그린다.
  branchScope: TaskEditorBranchScope | null;
  onBranchScopeChange: (scope: TaskEditorBranchScope | null) => void;
  // 미리보기에서 블록을 눌러 넘어온 경우 포커스할 블록 index — 현재 스코프
  // 기준(루트면 raw, 드릴다운이면 세그먼트 로컬). 소비 후 onFocusConsumed 로
  // 부모가 비워, 탭 재진입 시 중복 강조를 막는다.
  focusBlockIndex?: number | null;
  onFocusConsumed?: () => void;
}

export default function TaskEditorWorkspace({
  editorState,
  onChangeEditorState,
  branchScope,
  onBranchScopeChange,
  focusBlockIndex,
  onFocusConsumed,
}: TaskEditorWorkspaceProps): JSX.Element {
  const [blocks, setBlocksInternal] = useState<TaskEditorBlock[]>(
    editorState.blocks
  );

  // 외부 prop 동기화: editorState.blocks 가 다른 ref 로 바뀌면 내부 state 도 교체
  const prevBlocksRef = useRef(editorState.blocks);
  useEffect(() => {
    if (editorState.blocks !== prevBlocksRef.current) {
      prevBlocksRef.current = editorState.blocks;
      setBlocksInternal(editorState.blocks);
    }
  }, [editorState.blocks]);

  // 핸들러가 매 render 마다 새 closure 가 되지 않도록, 최신 값을 ref 로 노출.
  // 핸들러는 useCallback([] deps) 로 안정화 후 ref 를 통해 최신 데이터를 읽음.
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const onChangeEditorStateRef = useRef(onChangeEditorState);
  onChangeEditorStateRef.current = onChangeEditorState;

  // 내부 blocks 변경 → 부모로 전파
  useEffect(() => {
    if (blocks === prevBlocksRef.current) return;
    prevBlocksRef.current = blocks;
    onChangeEditorStateRef.current({
      ...editorStateRef.current,
      blocks,
    });
  }, [blocks]);

  // ─── 분기 드릴다운 스코프 ─────────────────────────────────────────────
  // null = 루트(활동지 전체). 값이 있으면 그 분기 블록/옵션의 세그먼트(내부
  // 블록)만 워킹셋으로 노출하고, 모든 핸들러·드래그·렌더가 그 위에서 동작한다.
  // undo 스냅샷은 루트 레벨 그대로라 스코프 경계를 넘어 동작한다.
  // 상태는 부모(패널) 소유 — 미리보기 탭과 공유.
  const branchScopeRef = useRef(branchScope);
  branchScopeRef.current = branchScope;
  const onBranchScopeChangeRef = useRef(onBranchScopeChange);
  onBranchScopeChangeRef.current = onBranchScopeChange;

  const scopedBlocks = branchScope
    ? getTaskEditorBranchSegmentBlocks(
        blocks,
        branchScope.blockIndex,
        branchScope.optionIndex
      )
    : blocks;
  const scopedBlocksRef = useRef(scopedBlocks);
  scopedBlocksRef.current = scopedBlocks;

  // 스코프 인지 setter — 값/함수형 updater 모두 지원 (드래그 hook 요건).
  // 스코프 중에는 세그먼트 교체를 루트 blocks 로 들어 올린다(lift).
  const setScopedBlocks: React.Dispatch<
    React.SetStateAction<TaskEditorBlock[]>
  > = useCallback((action) => {
    setBlocksInternal((rootPrev) => {
      const scope = branchScopeRef.current;
      if (!scope) {
        return typeof action === "function" ? action(rootPrev) : action;
      }
      const prevScoped = getTaskEditorBranchSegmentBlocks(
        rootPrev,
        scope.blockIndex,
        scope.optionIndex
      );
      const nextScoped =
        typeof action === "function" ? action(prevScoped) : action;
      return applyBranchSegmentBlocksChange(
        rootPrev,
        scope.blockIndex,
        scope.optionIndex,
        nextScoped
      );
    });
  }, []);

  // 블록 변경(undo·agent patch 등)으로 스코프 대상이 사라지면 루트로 복귀.
  useEffect(() => {
    if (!branchScope) return;
    const target = blocks[branchScope.blockIndex];
    const valid =
      target?.action?.type === "button_branch" &&
      branchScope.optionIndex < target.action.options.length;
    if (!valid) onBranchScopeChangeRef.current(null);
  }, [blocks, branchScope]);

  // 루트 뷰 파생값 — 그룹(분기 묶음) 구조와 사람이 읽는 블록 라벨.
  const blockGroups = useMemo(
    () => buildTaskEditorBlockGroups(blocks),
    [blocks]
  );
  const displayLabels = useMemo(
    () => getTaskEditorBlockDisplayLabels(blocks),
    [blocks]
  );

  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<TaskEditorDeleteTarget | null>(null);
  const [highlightedBlockIndex, setHighlightedBlockIndex] = useState<
    number | null
  >(null);
  const [jsonInspect, setJsonInspect] = useState<{
    block: TaskEditorBlock;
    label: string;
  } | null>(null);

  const pendingDeleteTargetLabel = pendingDeleteTarget
    ? taskEditorDeleteTargetLabelMap[pendingDeleteTarget.type]
    : undefined;

  // 루트에서 분기 블록을 지우면 세그먼트(내부 블록)도 함께 삭제됨을 고지.
  const pendingDeleteSubtitle = (() => {
    if (!pendingDeleteTarget) return undefined;
    if (pendingDeleteTarget.type === "block" && !branchScope) {
      const target = blocks[pendingDeleteTarget.blockIndex];
      if (target?.action?.type === "button_branch") {
        const run = getTaskEditorBranchRunSpan(
          blocks,
          pendingDeleteTarget.blockIndex
        );
        const segmentCount = run ? run.end - run.start - 1 : 0;
        if (segmentCount > 0) {
          return `분기 내부 블록 ${segmentCount}개가 함께 삭제됩니다.`;
        }
      }
    }
    return pendingDeleteTargetLabel
      ? `삭제 대상 type: ${pendingDeleteTargetLabel}`
      : undefined;
  })();

  // ─── undo ────────────────────────────────────────────────────────────────
  // getCurrentState / applyState 를 stable useCallback 으로 만들어
  // undoHistory 가 반환하는 pushSnapshot / pushSnapshotForTextEdit 도 stable 하게.

  const getCurrentStateStable = useCallback(
    () => ({ ...editorStateRef.current, blocks: blocksRef.current }),
    []
  );
  const applyStateStable = useCallback((state: TaskEditorState) => {
    prevBlocksRef.current = state.blocks;
    setBlocksInternal(state.blocks);
    onChangeEditorStateRef.current(state);
  }, []);

  const undoHistory = useTaskEditorUndoHistory({
    getCurrentState: getCurrentStateStable,
    applyState: applyStateStable,
  });
  const {
    pushSnapshot,
    pushSnapshotForTextEdit,
    resetTextEditGrouping,
    undo,
    hasHistory,
  } = undoHistory;

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (
        !hasHistory ||
        event.defaultPrevented ||
        event.repeat ||
        !isTaskEditorUndoShortcut(event)
      ) {
        return;
      }

      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", handleWindowKeyDown);
    return () => window.removeEventListener("keydown", handleWindowKeyDown);
  }, [hasHistory, undo]);

  // ─── drag ────────────────────────────────────────────────────────────────

  const onDragReorder = useCallback(() => {
    resetTextEditGrouping();
    pushSnapshot();
  }, [resetTextEditGrouping, pushSnapshot]);

  // 드롭으로 재정렬된 블록을 기존 하이라이트(옅은 파랑 틴트)로 강조한다.
  // 이미 화면에 보이는 위치이므로 스크롤은 억제한다.
  const suppressHighlightScrollRef = useRef(false);
  const onBlockReordered = useCallback((toIndex: number) => {
    suppressHighlightScrollRef.current = true;
    setHighlightedBlockIndex(toIndex);
  }, []);

  const {
    draggedBlockIndex,
    blockDropPreview,
    handleBlockDragStart,
    handleBlockDragOver,
    handleBlockDrop,
    handleBlockDragEnd,
    handleBlockCardDragOver,
    handleBlockCardDrop,
  } = useTaskEditorBlockDrag({
    blocks: scopedBlocks,
    setBlocks: setScopedBlocks,
    onBeforeReorder: onDragReorder,
    onReorder: onBlockReordered,
    // 루트에서는 분기 블록 드래그가 세그먼트 run 과 한 묶음으로 움직인다.
    // 스코프(세그먼트 내부)에서는 평범한 단일 블록 재정렬.
    ...(branchScope
      ? {}
      : {
          getReorderIndex: getTaskEditorGroupReorderTarget,
          reorder: reorderTaskEditorBlockGroup,
        }),
  });

  // ─── handlers (모두 stable — blocksRef 를 통해 최신 blocks 읽음) ────────────

  const commit = useCallback(
    (next: TaskEditorBlock[]) => {
      if (next === scopedBlocksRef.current) return;
      resetTextEditGrouping();
      pushSnapshot();
      setScopedBlocks(next);
    },
    [resetTextEditGrouping, pushSnapshot, setScopedBlocks]
  );

  const commitTextEdit = useCallback(
    (groupKey: string, next: TaskEditorBlock[]) => {
      if (next === scopedBlocksRef.current) return;
      pushSnapshotForTextEdit(groupKey);
      setScopedBlocks(next);
    },
    [pushSnapshotForTextEdit, setScopedBlocks]
  );

  // ─── 새 블록 하이라이트 + 스크롤 ──────────────────────────────────────────
  // handleAddBlock 호출 시 정확한 insertionIndex 를 기억해서 그 블록을 잠시
  // 강조하고 화면을 그쪽으로 이동. + 버튼이 블록 사이에 있는 경우에도 올바른
  // 위치를 가리키도록 한다.
  const blockCardRefs = useRef<Map<number, HTMLElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // 드릴다운 진입/복귀 스크롤 — 같은 스크롤 컨테이너에서 내용만 바뀌므로
  // 그대로 두면 이전 위치가 남는다. 진입 시 스코프 맨 위에서 시작하고,
  // 루트로 돌아오면 진입 전 위치를 복원한다.
  const rootScrollTopRef = useRef(0);
  const prevScopeForScrollRef = useRef<TaskEditorBranchScope | null>(
    branchScope
  );
  useEffect(() => {
    const prev = prevScopeForScrollRef.current;
    prevScopeForScrollRef.current = branchScope;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (!prev && branchScope) {
      rootScrollTopRef.current = container.scrollTop;
      container.scrollTop = 0;
    } else if (prev && !branchScope) {
      container.scrollTop = rootScrollTopRef.current;
    }
  }, [branchScope]);

  // 드래그 중 패널 상/하단 가장자리에 다가가면 자동 스크롤 (긴 리스트에서 멀리
  // 떨어진 위치로 드롭 가능하게).
  const dragAutoScroll = useTaskEditorDragAutoScroll(scrollContainerRef);
  useEffect(() => {
    if (draggedBlockIndex === null) {
      dragAutoScroll.stop();
    }
  }, [draggedBlockIndex, dragAutoScroll]);

  const handleAddBlock = useCallback(
    (insertionIndex?: number, type: TaskEditorBlockType = "body") => {
      const current = scopedBlocksRef.current;
      const idx =
        typeof insertionIndex === "number"
          ? Math.max(0, Math.min(insertionIndex, current.length))
          : current.length;
      commit(applyAddBlock(current, insertionIndex, type));
      setHighlightedBlockIndex(idx);
    },
    [commit]
  );

  // 하이라이트 자동 해제
  useEffect(() => {
    if (highlightedBlockIndex === null) return;
    const t = window.setTimeout(() => setHighlightedBlockIndex(null), 1500);
    return () => window.clearTimeout(t);
  }, [highlightedBlockIndex]);

  // 하이라이트된 블록으로 스크롤 (DOM 업데이트 후). 블록 아래로 60vh 를 남겨 화면
  // 중앙보다 살짝 위에 자리잡게 한다 (추가/하이라이트 시 이상적인 정착 위치).
  // 단 마지막 블록은 하단 trailing 여백(pb) 만큼만 위로 올라갈 수 있어 살짝 더
  // 아래에 멈춘다 — 끝까지 스크롤했을 때의 빈 공간을 줄이려고 pb 를 60vh 보다 작게
  // 두었기 때문 (의도된 동작). 또 블록이 큰 경우 상단이 잘리지 않도록 위쪽(상단
  // 12px)에 고정한다.
  useEffect(() => {
    if (highlightedBlockIndex === null) return;
    // 드롭 재정렬 강조는 스크롤 없이 틴트만 (이미 보이는 위치)
    if (suppressHighlightScrollRef.current) {
      suppressHighlightScrollRef.current = false;
      return;
    }
    const container = scrollContainerRef.current;
    const node = blockCardRefs.current.get(highlightedBlockIndex);
    if (!container || !node) return;
    const containerTop = container.getBoundingClientRect().top;
    const nodeRect = node.getBoundingClientRect();
    const blockTop = nodeRect.top - containerTop + container.scrollTop;
    const blockBottom = nodeRect.bottom - containerTop + container.scrollTop;
    const leave60vhBelow =
      blockBottom - (container.clientHeight - window.innerHeight * 0.6);
    const pinNearTop = blockTop - 12;
    container.scrollTo({
      top: Math.max(0, Math.min(leave60vhBelow, pinNearTop)),
      behavior: "smooth",
    });
  }, [highlightedBlockIndex]);

  // 미리보기에서 블록을 눌러 넘어오면 해당 블록을 강조 + 스크롤한다. 위의 새 블록
  // 추가용 강조/스크롤 메커니즘(highlightedBlockIndex)을 그대로 재사용한다. 소비
  // 즉시 부모 상태를 비워(onFocusConsumed) 탭 재진입 시 중복 강조를 막는다.
  useEffect(() => {
    if (focusBlockIndex == null) return;
    // index 는 현재 스코프 기준(부모가 스코프 전환과 함께 조율) — 그대로 강조.
    setHighlightedBlockIndex(focusBlockIndex);
    onFocusConsumed?.();
  }, [focusBlockIndex, onFocusConsumed]);

  const handleRemoveBlock = useCallback(
    (blockIndex: number) =>
      // 세그먼트 스코프에서는 마지막 블록을 지워 빈 세그먼트가 될 수 있다.
      commit(
        applyRemoveBlock(
          scopedBlocksRef.current,
          blockIndex,
          branchScopeRef.current !== null
        )
      ),
    [commit]
  );

  // 분기 옵션의 "내부 블록 편집" — 드릴다운 스코프 진입. 깊이 1: 스코프 안에서는
  // 분기형 action 자체를 추가할 수 없어 재진입 경로가 없다.
  // focusLocalIndex 가 있으면(미니 블록 행 클릭) 스코프 진입과 함께 그 블록을
  // 하이라이트 + 스크롤한다 (스코프 내 0-based — 기존 강조 메커니즘 재사용).
  const handleOpenBranchOption = useCallback(
    (blockIndex: number, optionIndex: number, focusLocalIndex?: number) => {
      if (branchScopeRef.current) return;
      onBranchScopeChangeRef.current({ blockIndex, optionIndex });
      if (typeof focusLocalIndex === "number") {
        setHighlightedBlockIndex(focusLocalIndex);
      }
    },
    []
  );

  const handleAddAction = useCallback(
    (blockIndex: number, type: TaskEditorActionComponentType) =>
      commit(applyAddAction(scopedBlocksRef.current, blockIndex, type)),
    [commit]
  );

  const handleRemoveAction = useCallback(
    (blockIndex: number) =>
      commit(applyRemoveActionComponent(scopedBlocksRef.current, blockIndex)),
    [commit]
  );

  const handleDescriptionTypeChange = useCallback(
    (blockIndex: number, nextType: TaskEditorDescriptionType) =>
      commit(
        applyDescriptionTypeChange(
          scopedBlocksRef.current,
          blockIndex,
          nextType
        )
      ),
    [commit]
  );

  const handleActionComponentTypeChange = useCallback(
    (blockIndex: number, nextType: TaskEditorActionComponentType) =>
      commit(
        applyActionComponentTypeChange(
          scopedBlocksRef.current,
          blockIndex,
          nextType
        )
      ),
    [commit]
  );

  const handleDescriptionContentChange = useCallback(
    (blockIndex: number, content: string, groupKey: string) =>
      commitTextEdit(
        groupKey,
        applyDescriptionContentChange(
          scopedBlocksRef.current,
          blockIndex,
          content
        )
      ),
    [commitTextEdit]
  );

  const handleDescriptionImageUrlChange = useCallback(
    (blockIndex: number, imageUrl: string) =>
      commitTextEdit(
        `description-image-url:${blockIndex}`,
        applyDescriptionImageUrlChange(
          scopedBlocksRef.current,
          blockIndex,
          imageUrl
        )
      ),
    [commitTextEdit]
  );

  const handleAddLabelPair = useCallback(
    (blockIndex: number) =>
      commit(applyAddLabelPair(scopedBlocksRef.current, blockIndex)),
    [commit]
  );

  const handleRemoveLabelPair = useCallback(
    (blockIndex: number, pairIndex: number) =>
      commit(
        applyRemoveLabelPair(scopedBlocksRef.current, blockIndex, pairIndex)
      ),
    [commit]
  );

  const handleLabelPairChange = useCallback(
    (
      blockIndex: number,
      pairIndex: number,
      key: "label" | "text",
      value: string
    ) =>
      commitTextEdit(
        `label-pair:${blockIndex}:${pairIndex}:${key}`,
        applyLabelPairChange(
          scopedBlocksRef.current,
          blockIndex,
          pairIndex,
          key,
          value
        )
      ),
    [commitTextEdit]
  );

  const handleBlockTypeChange = useCallback(
    (blockIndex: number, nextType: TaskEditorBlockType) =>
      commit(
        applyBlockTypeChange(scopedBlocksRef.current, blockIndex, nextType)
      ),
    [commit]
  );

  const handleReferenceTypeChange = useCallback(
    (blockIndex: number, referenceType: TaskEditorReferenceType) =>
      commit(
        applyReferenceTypeChange(
          scopedBlocksRef.current,
          blockIndex,
          referenceType
        )
      ),
    [commit]
  );

  const handleReferencePresetTextChange = useCallback(
    (blockIndex: number, text: string) =>
      commitTextEdit(
        `reference-preset-text:${blockIndex}`,
        applyReferencePresetTextChange(
          scopedBlocksRef.current,
          blockIndex,
          text
        )
      ),
    [commitTextEdit]
  );

  const handleAddTextInputField = useCallback(
    (blockIndex: number) =>
      commit(applyAddTextInputField(scopedBlocksRef.current, blockIndex)),
    [commit]
  );

  const handleRemoveTextInputField = useCallback(
    (blockIndex: number, fieldIndex: number) =>
      commit(
        applyRemoveTextInputField(
          scopedBlocksRef.current,
          blockIndex,
          fieldIndex
        )
      ),
    [commit]
  );

  const handleTextInputFieldChange = useCallback(
    (
      blockIndex: number,
      fieldIndex: number,
      key: "label" | "placeholder" | "help_text",
      value: string
    ) =>
      commitTextEdit(
        `text-input-field:${blockIndex}:${fieldIndex}:${key}`,
        applyTextInputFieldChange(
          scopedBlocksRef.current,
          blockIndex,
          fieldIndex,
          key,
          value
        )
      ),
    [commitTextEdit]
  );

  const handleTextInputFieldOptionalToggle = useCallback(
    (blockIndex: number, fieldIndex: number, optional: boolean) =>
      commit(
        applyTextInputFieldOptionalToggle(
          scopedBlocksRef.current,
          blockIndex,
          fieldIndex,
          optional
        )
      ),
    [commit]
  );

  const handleAddButtonOption = useCallback(
    (blockIndex: number) =>
      commit(applyAddButtonOption(scopedBlocksRef.current, blockIndex)),
    [commit]
  );

  const handleRemoveButtonOption = useCallback(
    (blockIndex: number, optionIndex: number) =>
      commit(
        applyRemoveButtonOption(
          scopedBlocksRef.current,
          blockIndex,
          optionIndex
        )
      ),
    [commit]
  );

  const handleButtonOptionTextChange = useCallback(
    (blockIndex: number, optionIndex: number, text: string) =>
      commitTextEdit(
        `button-option-text:${blockIndex}:${optionIndex}`,
        applyButtonOptionTextChange(
          scopedBlocksRef.current,
          blockIndex,
          optionIndex,
          text
        )
      ),
    [commitTextEdit]
  );

  const handleButtonQuizOptionCorrectToggle = useCallback(
    (blockIndex: number, optionIndex: number, isCorrect: boolean) =>
      commit(
        applyButtonQuizOptionCorrectToggle(
          scopedBlocksRef.current,
          blockIndex,
          optionIndex,
          isCorrect
        )
      ),
    [commit]
  );

  const handleButtonDefaultAllowMultipleChange = useCallback(
    (blockIndex: number, allow: boolean) =>
      commit(
        applyButtonDefaultAllowMultipleChange(
          scopedBlocksRef.current,
          blockIndex,
          allow
        )
      ),
    [commit]
  );

  const handleAudioFieldChange = useCallback(
    (
      blockIndex: number,
      key: "artwork" | "audio_url" | "extra_audio_url",
      value: string
    ) =>
      commitTextEdit(
        `audio-field:${blockIndex}:${key}`,
        applyAudioFieldChange(scopedBlocksRef.current, blockIndex, key, value)
      ),
    [commitTextEdit]
  );

  const handleAudioArtworkFileChange = useCallback(
    async (blockIndex: number, file: File) => {
      try {
        const imageDataUrl = await convertTaskEditorImageFileToDataUrl(file);
        const currentBlocks = scopedBlocksRef.current;
        const currentAction = currentBlocks[blockIndex]?.action;
        if (
          !currentAction ||
          currentAction.type !== "audio" ||
          currentAction.artwork === imageDataUrl
        ) {
          return;
        }
        commit(
          applyAudioFieldChange(
            currentBlocks,
            blockIndex,
            "artwork",
            imageDataUrl
          )
        );
      } catch {
        // 이미지 업로드 실패는 조용히 처리 (image 컴포넌트와 동일)
      }
    },
    [commit]
  );

  const handleImageFileChange = useCallback(
    async (blockIndex: number, file: File) => {
      try {
        const imageDataUrl = await convertTaskEditorImageFileToDataUrl(file);
        const currentBlocks = scopedBlocksRef.current;
        const currentDescription = currentBlocks[blockIndex]?.description;

        if (
          !currentDescription ||
          currentDescription.type !== "image" ||
          currentDescription.image_url === imageDataUrl
        ) {
          return;
        }

        commit(
          applyDescriptionImageUrlChange(
            currentBlocks,
            blockIndex,
            imageDataUrl
          )
        );
      } catch {
        // 이미지 업로드 실패는 조용히 처리
      }
    },
    [commit]
  );

  const handleOpenDeleteTargetModal = useCallback(
    (target: TaskEditorDeleteTarget) => {
      setPendingDeleteTarget(target);
    },
    []
  );

  const handleCloseDeleteTargetModal = useCallback(() => {
    setPendingDeleteTarget(null);
  }, []);

  const handleConfirmDeleteTarget = useCallback(() => {
    if (!pendingDeleteTarget) return;

    if (pendingDeleteTarget.type === "block") {
      handleRemoveBlock(pendingDeleteTarget.blockIndex);
    } else {
      handleRemoveAction(pendingDeleteTarget.blockIndex);
    }

    setPendingDeleteTarget(null);
  }, [pendingDeleteTarget, handleRemoveBlock, handleRemoveAction]);

  // 블록 추가 + 메뉴 (헤더/바디 선택). 맨 위·블록 사이/뒤에서 공용으로 재사용.
  const renderAddBlockMenu = (
    insertionIndex: number,
    triggerTestId: string,
    headerTestId: string,
    bodyTestId: string
  ) => (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{
          root: {
            "aria-label": "블록 추가",
            "data-testid": triggerTestId,
            sx: taskEditorAddBlockButtonSx,
          },
        }}
      >
        <AddIcon size={16} />
      </MenuButton>
      <Menu placement="bottom" sx={taskEditorAddBlockMenuSx}>
        <MenuItem
          data-testid={headerTestId}
          onClick={() => handleAddBlock(insertionIndex, "header")}
        >
          헤더
        </MenuItem>
        <MenuItem
          data-testid={bodyTestId}
          onClick={() => handleAddBlock(insertionIndex, "body")}
        >
          바디
        </MenuItem>
      </Menu>
    </Dropdown>
  );

  // 블록 카드 + (루트의 분기 그룹이면) 세그먼트 컴팩트 목록 + 뒤따르는 추가/
  // 드롭 행. 루트는 그룹(분기 묶음) 단위로, 스코프는 세그먼트 블록 단위로 호출.
  // afterInsertionIndex 는 이 entry 뒤의 삽입 위치 (그룹이면 run 끝).
  const renderBlockEntry = (
    block: TaskEditorBlock,
    blockIndex: number,
    afterInsertionIndex: number,
    segments: TaskEditorBranchSegment[] | null
  ) => {
    const isDraggedBlock = draggedBlockIndex === blockIndex;
    const isAfterPreviewActive = isSameTaskEditorBlockDropPreview(
      blockDropPreview,
      { insertionIndex: afterInsertionIndex }
    );

    const isHighlighted = highlightedBlockIndex === blockIndex;
    const isScoped = branchScope !== null;
    const blockLabel = isScoped
      ? `블록 ${blockIndex + 1}`
      : (displayLabels[blockIndex] ?? `블록 ${blockIndex + 1}`);
    const jsonLabel =
      isScoped && branchScope
        ? `${
            displayLabels[branchScope.blockIndex] ??
            `블록 ${branchScope.blockIndex + 1}`
          } · 옵션 ${branchScope.optionIndex + 1} · ${blockIndex + 1}`
        : blockLabel;
    const branchAction =
      block.action?.type === "button_branch" ? block.action : null;
    const branchSegmentCounts =
      segments && branchAction
        ? branchAction.options.map((_, optionIdx) => {
            const segment = segments.find((s) => s.optionIndex === optionIdx);
            return segment ? segment.end - segment.start : 0;
          })
        : null;

    return (
      <React.Fragment key={`block-${blockIndex}`}>
        <Sheet
          ref={(el: HTMLElement | null) => {
            if (el) blockCardRefs.current.set(blockIndex, el);
            else blockCardRefs.current.delete(blockIndex);
          }}
          sx={{
            ...taskEditorBlockCardSx,
            // 드래그 중인 원본 카드는 제자리에 흐릿하게 남겨 "옮기는
            // 중" 임을 표시한다. 커서를 따라가는 건 드래그 고스트(카드
            // 스냅샷)이므로 원본을 띄우거나 이동시키지 않는다.
            opacity: isDraggedBlock ? 0.4 : 1,
            // 새 블록 하이라이트: 카드 전체에 매우 옅은 파랑 톤만 살짝
            // 입혀 자연스럽게 강조. 1.5초 뒤 자동 해제되며 천천히 fade.
            backgroundColor: isHighlighted
              ? "rgba(15, 85, 167, 0.04)"
              : taskEditorBlockCardSx.backgroundColor,
            boxShadow: taskEditorBlockCardSx.boxShadow,
            borderColor: isHighlighted
              ? "rgba(15, 85, 167, 0.10)"
              : isDraggedBlock
                ? theme.vars.palette.primary.soft
                : taskEditorBlockCardSx.borderColor,
            transition:
              "background-color 0.7s ease, border-color 0.2s ease, opacity 0.2s ease",
            zIndex: 1,
            mb: 0,
          }}
          data-testid="task-editor-block-card"
          data-block-index={blockIndex}
          data-dragging={isDraggedBlock ? "true" : "false"}
          data-drop-preview="none"
          onDragOver={(event) => handleBlockCardDragOver(event, blockIndex)}
          onDrop={(event) => handleBlockCardDrop(event, blockIndex)}
        >
          <Stack spacing={1.75} sx={{ position: "relative" }}>
            <BlockHeader
              blockIndex={blockIndex}
              label={blockLabel}
              blockType={block.type}
              isDragging={isDraggedBlock}
              canRemove={isScoped ? true : blocks.length > 1}
              onBlockTypeChange={(nextType) =>
                handleBlockTypeChange(blockIndex, nextType)
              }
              onDragStart={(event) =>
                handleBlockDragStart(
                  event,
                  blockIndex,
                  blockCardRefs.current.get(blockIndex)
                )
              }
              onDragEnd={handleBlockDragEnd}
              onOpenJson={() =>
                setJsonInspect({
                  block,
                  label: jsonLabel,
                })
              }
              onDelete={() =>
                handleOpenDeleteTargetModal({
                  type: "block",
                  blockIndex,
                })
              }
            />

            <BlockBody
              block={block}
              blockIndex={blockIndex}
              excludeBranchAction={isScoped}
              branchSegmentCounts={branchSegmentCounts}
              onAddAction={handleAddAction}
              onRemoveAction={(targetBlockIndex) =>
                handleOpenDeleteTargetModal({
                  type: "action",
                  blockIndex: targetBlockIndex,
                })
              }
              onDescriptionTypeChange={handleDescriptionTypeChange}
              onActionComponentTypeChange={handleActionComponentTypeChange}
              onDescriptionContentChange={handleDescriptionContentChange}
              onDescriptionImageUrlChange={handleDescriptionImageUrlChange}
              onAddLabelPair={handleAddLabelPair}
              onRemoveLabelPair={handleRemoveLabelPair}
              onLabelPairChange={handleLabelPairChange}
              onReferenceTypeChange={handleReferenceTypeChange}
              onReferencePresetTextChange={handleReferencePresetTextChange}
              onAddTextInputField={handleAddTextInputField}
              onRemoveTextInputField={handleRemoveTextInputField}
              onTextInputFieldChange={handleTextInputFieldChange}
              onTextInputFieldOptionalToggle={
                handleTextInputFieldOptionalToggle
              }
              onImageFileChange={handleImageFileChange}
              onAddButtonOption={handleAddButtonOption}
              onRemoveButtonOption={handleRemoveButtonOption}
              onButtonOptionTextChange={handleButtonOptionTextChange}
              onButtonQuizOptionCorrectToggle={
                handleButtonQuizOptionCorrectToggle
              }
              onOpenBranchOption={handleOpenBranchOption}
              onButtonDefaultAllowMultipleChange={
                handleButtonDefaultAllowMultipleChange
              }
              onAudioFieldChange={handleAudioFieldChange}
              onAudioArtworkFileChange={handleAudioArtworkFileChange}
              onTextEditBlur={undoHistory.resetTextEditGrouping}
            />
          </Stack>
        </Sheet>

        {/* 루트 뷰: 분기 카드 아래 세그먼트(내부 블록) 영역 — 플로우차트식
            가로 컬럼. 옵션마다(빈 옵션 포함) 컬럼 하나씩 갈라졌다가 하단에서
            "▼ 합류" 로 다시 모인다. 패널/행 클릭 시 그 옵션 드릴다운 진입,
            풀 편집은 드릴다운에서. */}
        {segments && branchAction && branchAction.options.length > 0 && (
          <Box
            sx={taskEditorSegmentGroupSx}
            data-testid={`task-editor-segment-group-${blockIndex}`}
          >
            <TaskEditorBranchFlow
              optionCount={branchAction.options.length}
              getColumnTestId={(optionIndex) =>
                `task-editor-segment-column-${blockIndex}-${optionIndex}`
              }
              mergeTestId={`task-editor-segment-merge-${blockIndex}`}
              pagerTestIdPrefix={`task-editor-segment-pager-${blockIndex}`}
              renderColumnContent={(optionIndex) => {
                const segment =
                  segments.find((s) => s.optionIndex === optionIndex) ?? null;
                const optionText =
                  branchAction.options[optionIndex]?.text ?? "";
                return (
                  <Sheet
                    sx={taskEditorSegmentPanelSx}
                    data-testid={`task-editor-segment-panel-${blockIndex}-${optionIndex}`}
                  >
                    {/* 헤더 줄 전체가 드릴다운 클릭 영역 — chevron 은 시각
                        표시. 버튼 중첩(button in button)을 피해 행 자체를
                        button 으로 만든다. */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      component="button"
                      type="button"
                      aria-label={`옵션 ${optionIndex + 1} 편집`}
                      data-testid={`task-editor-segment-panel-edit-${blockIndex}-${optionIndex}`}
                      onClick={() =>
                        handleOpenBranchOption(blockIndex, optionIndex)
                      }
                      sx={{
                        minWidth: 0,
                        p: 0,
                        mx: -0.5,
                        px: 0.5,
                        py: 0.25,
                        border: "none",
                        borderRadius: "6px",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                        "&:hover": {
                          backgroundColor: theme.vars.palette.Background.Level2,
                        },
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          minWidth: 0,
                          fontSize: "12px",
                          fontWeight: 600,
                          color: theme.vars.palette.Content.Medium,
                        }}
                      >
                        {`옵션 ${optionIndex + 1}${
                          optionText ? ` · ${optionText}` : ""
                        }`}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          flexShrink: 0,
                          color: theme.vars.palette.Content.Medium,
                        }}
                      >
                        <span
                          style={{ fontSize: "14px" }}
                          className="material-symbols-rounded"
                        >
                          chevron_right
                        </span>
                      </Box>
                    </Stack>
                    {segment ? (
                      blocks
                        .slice(segment.start, segment.end)
                        .map((segmentBlock, offset) => {
                          const segmentBlockIndex = segment.start + offset;
                          return (
                            <Box
                              key={segmentBlockIndex}
                              component="button"
                              type="button"
                              data-testid={`task-editor-segment-block-${segmentBlockIndex}`}
                              // 드릴다운 진입 + 이 블록(스코프 내 offset)
                              // 하이라이트·스크롤.
                              onClick={() =>
                                handleOpenBranchOption(
                                  blockIndex,
                                  optionIndex,
                                  offset
                                )
                              }
                              sx={taskEditorSegmentRowSx}
                            >
                              <Typography
                                noWrap
                                sx={{
                                  minWidth: 0,
                                  flex: 1,
                                  fontSize: "12px",
                                  color: theme.vars.palette.Content.Medium,
                                }}
                              >
                                {getTaskEditorBlockSummaryText(segmentBlock)}
                              </Typography>
                            </Box>
                          );
                        })
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        data-testid={`task-editor-segment-empty-${blockIndex}-${optionIndex}`}
                        onClick={() =>
                          handleOpenBranchOption(blockIndex, optionIndex)
                        }
                        sx={taskEditorSegmentRowSx}
                      >
                        <Typography
                          noWrap
                          sx={{
                            minWidth: 0,
                            fontSize: "12px",
                            color: theme.vars.palette.Content.Weak,
                          }}
                        >
                          내부 블록 없음 — 선택 시 바로 합류
                        </Typography>
                      </Box>
                    )}
                  </Sheet>
                );
              }}
            />
          </Box>
        )}

        <Stack
          sx={taskEditorAddBlockButtonWrapSx}
          // 블록 사이 영역(+ 버튼 행) 전체를 드롭 타겟으로 — 아래로
          // 끌어 여기서 놓아도 재정렬이 커밋되도록 dead zone 을 없앤다.
          onDragOver={(event) =>
            handleBlockDragOver(event, {
              insertionIndex: afterInsertionIndex,
            })
          }
          onDrop={(event) =>
            handleBlockDrop(event, {
              insertionIndex: afterInsertionIndex,
            })
          }
        >
          {renderAddBlockMenu(
            afterInsertionIndex,
            "task-editor-add-block-between",
            `task-editor-add-block-header-${blockIndex}`,
            `task-editor-add-block-body-${blockIndex}`
          )}
          <Box
            data-testid="task-editor-block-drop-zone"
            data-drop-preview={isAfterPreviewActive ? "active" : "inactive"}
            sx={{
              ...taskEditorBlockDropZoneSx,
              // 삽입 지점에서만 슬롯처럼 벌어져 아래 블록을 밀어낸다.
              // (비활성은 0 — 드래그 시작 시 간격 점프 방지)
              height: isAfterPreviewActive ? "32px" : "0px",
              opacity: isAfterPreviewActive ? 1 : 0,
              border: "1px dashed",
              borderColor: isAfterPreviewActive
                ? theme.vars.palette.primary.soft
                : "transparent",
              backgroundColor: isAfterPreviewActive
                ? "rgba(15, 85, 167, 0.06)"
                : "transparent",
            }}
          />
        </Stack>
      </React.Fragment>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        ref={scrollContainerRef}
        onDragOver={(event) => {
          if (draggedBlockIndex !== null) {
            dragAutoScroll.update(event.clientY);
          }
        }}
        sx={{
          ...panelScrollbarSx,
          px: "12px",
          // 도움말 버튼 행 제거 후 첫 블록이 탭 바에 붙어 보여 여백 보강.
          pt: "42px",
          // 마지막 블록 아래 trailing 여백. 추가/하이라이트 블록을 위로 끌어올릴
          // 스크롤 여유분이자, 끝까지 스크롤했을 때 남는 빈 공간이기도 하다. 60vh
          // 는 끝까지 스크롤 시 공간이 과해서 40vh 로 제한 (자동 스크롤 목표 60vh
          // 와 분리 — 마지막 블록은 이 값만큼만 위로 올라감).
          pb: "40vh",
          height: "100%",
          minHeight: 0,
          fontSize: "13px",
          "& input": { fontSize: "13px" },
          "& textarea": { fontSize: "13px" },
        }}
        data-testid="task-editor-block-panel"
      >
        <Stack
          spacing={2}
          sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}
        >
          {/* 드릴다운 브레드크럼은 패널(TaskWorkspacePanel)의 탭 바 아래
              고정 하이러키 바가 담당 — 스크롤 영역 안에는 두지 않는다. */}
          <Stack spacing={0}>
            {/* 맨 위(index 0) 삽입은 첫 블록 카드 윗부분 hover 로 처리한다.
                빈 윗공간에 뜨던 before-first 드롭존 박스는 제거. */}
            {branchScope && scopedBlocks.length === 0 ? (
              <Stack
                sx={taskEditorAddBlockButtonWrapSx}
                data-testid="task-editor-branch-scope-empty"
              >
                <Typography
                  variant="Footnote"
                  sx={{
                    color: theme.vars.palette.Content.Weak,
                    fontSize: "12px",
                  }}
                >
                  이 옵션의 내부 블록이 없습니다. 선택 시 바로 다음 블록으로
                  합류합니다.
                </Typography>
                {renderAddBlockMenu(
                  0,
                  "task-editor-add-block-empty-scope",
                  "task-editor-add-block-header-scope-0",
                  "task-editor-add-block-body-scope-0"
                )}
              </Stack>
            ) : branchScope ? (
              scopedBlocks.map((block, blockIndex) =>
                renderBlockEntry(block, blockIndex, blockIndex + 1, null)
              )
            ) : (
              blockGroups.map((group) => {
                const lastSegment =
                  group.kind === "branch"
                    ? group.segments[group.segments.length - 1]
                    : undefined;
                return renderBlockEntry(
                  blocks[group.index],
                  group.index,
                  lastSegment ? lastSegment.end : group.index + 1,
                  group.kind === "branch" ? group.segments : null
                );
              })
            )}
            {!branchScope && <TaskEditorFooterBlocks />}
          </Stack>
        </Stack>
      </Box>

      <CustomModal
        open={Boolean(pendingDeleteTarget)}
        onClose={handleCloseDeleteTargetModal}
        title={
          pendingDeleteTargetLabel
            ? `${pendingDeleteTargetLabel}을 삭제할까요?`
            : "삭제할까요?"
        }
        subtitle={pendingDeleteSubtitle}
        actions={[
          {
            label: "삭제하기",
            variant: "Solid",
            color: "danger",
            onClick: handleConfirmDeleteTarget,
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: handleCloseDeleteTargetModal,
          },
        ]}
      />

      <TaskEditorBlockJsonModal
        target={jsonInspect}
        onClose={() => setJsonInspect(null)}
      />
    </>
  );
}

// ─── 푸터(마치기) 블록 묶음 — 편집 탭 참고용 ─────────────────────────────────
// blocks JSON·데이터 모델·Agent task_patch·저장 어디에도 들어가지 않는 순수 표시.
// 기존 블록 카드와 동일한 스타일에 disabled 느낌(흐림 + 비활성)을 입혀 하나로
// 퉁쳐 보여준다. 드래그·삭제·추가 불가, 아래에 + 버튼 없음. 실제 본문(소감
// 입력·마치기)은 미리보기 탭에서 확인.
function TaskEditorFooterBlocks() {
  return (
    <Sheet
      data-testid="task-editor-footer-block-group"
      aria-disabled
      sx={{
        ...taskEditorBlockCardSx,
        mb: 0,
        opacity: 0.55,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <IconButton size="sm" component="div" sx={{ cursor: "default" }}>
          <span
            style={{ fontSize: "18px" }}
            className="material-symbols-rounded"
          >
            drag_indicator
          </span>
        </IconButton>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          블록 푸터 묶음
        </Typography>
      </Stack>
    </Sheet>
  );
}

// ─── 블록 헤더 ────────────────────────────────────────────────────────────

interface BlockHeaderProps {
  blockIndex: number;
  // 사람이 읽는 블록 라벨 ("블록 3" — 메인 카운트 기준, 스코프에선 로컬 번호).
  label: string;
  blockType: TaskEditorBlockType;
  isDragging: boolean;
  canRemove: boolean;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onOpenJson: () => void;
  onDelete: () => void;
  onBlockTypeChange: (nextType: TaskEditorBlockType) => void;
}

function BlockHeader({
  blockIndex,
  label,
  blockType,
  isDragging,
  canRemove,
  onDragStart,
  onDragEnd,
  onOpenJson,
  onDelete,
  onBlockTypeChange,
}: BlockHeaderProps) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <IconButton
          size="sm"
          component="div"
          role="button"
          tabIndex={0}
          aria-label={`${label} 이동`}
          data-testid={`task-editor-drag-block-${blockIndex}`}
          draggable
          onDragStart={onDragStart}
          onDragEnd={(event) => {
            // 드래그 후 손잡이에 focus/active 잔상이 남아 헷갈리는 것 방지
            event.currentTarget.blur();
            onDragEnd();
          }}
          sx={{
            cursor: isDragging ? "grabbing" : "grab",
            // 손잡이는 hover/active/focus 시각 효과 없이 커서만 — 잔상 제거
            "--variant-hoverBg": "transparent",
            "--variant-activeBg": "transparent",
            "&:hover, &:active": { backgroundColor: "transparent" },
            "&:focus, &:focus-visible, &.Mui-focusVisible": {
              outline: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          <span
            style={{ fontSize: "18px" }}
            className="material-symbols-rounded"
          >
            drag_indicator
          </span>
        </IconButton>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {label}
        </Typography>
        {/* 블록 타입(헤더/바디) 표시 + 전환 — 중요 기능 아님, 라벨 영역의 작은 셀렉터 */}
        <Select<TaskEditorBlockType>
          size="sm"
          value={blockType}
          onChange={(_, value) => {
            if (!value || value === blockType) return;
            onBlockTypeChange(value);
          }}
          sx={{
            ...taskEditorSelectSx,
            minWidth: 84,
          }}
          slotProps={{
            button: {
              "data-testid": `task-editor-block-type-select-${blockIndex}`,
              sx: taskEditorSelectButtonSx,
            },
            listbox: { sx: taskEditorSelectListboxSx },
          }}
        >
          <Option value="header">헤더</Option>
          <Option value="body">바디</Option>
        </Select>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <IconButton
          size="sm"
          aria-label="블록 JSON 보기"
          data-testid={`task-editor-view-block-json-${blockIndex}`}
          onClick={onOpenJson}
        >
          <CodeIcon sx={{ fontSize: "18px" }} />
        </IconButton>
        {canRemove ? (
          <IconButton
            size="sm"
            aria-label="블록 삭제"
            data-testid={`task-editor-delete-block-${blockIndex}`}
            onClick={onDelete}
          >
            <span
              style={{ fontSize: "16px" }}
              className="material-symbols-rounded"
            >
              close
            </span>
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

// ─── 블록 본문 ────────────────────────────────────────────────────────────

interface BlockBodyProps {
  block: TaskEditorBlock;
  blockIndex: number;
  // 분기 드릴다운 스코프 안 — action 추가 메뉴/타입 셀렉터에서 분기형 제외 (깊이 1)
  excludeBranchAction: boolean;
  // 루트 뷰의 분기 블록에만 — 옵션별 세그먼트(내부 블록) 수. 그 외 null.
  branchSegmentCounts: number[] | null;
  onAddAction: (
    blockIndex: number,
    type: TaskEditorActionComponentType
  ) => void;
  onRemoveAction: (blockIndex: number) => void;
  onDescriptionTypeChange: (
    blockIndex: number,
    nextType: TaskEditorDescriptionType
  ) => void;
  onActionComponentTypeChange: (
    blockIndex: number,
    nextType: TaskEditorActionComponentType
  ) => void;
  onDescriptionContentChange: (
    blockIndex: number,
    content: string,
    groupKey: string
  ) => void;
  onDescriptionImageUrlChange: (blockIndex: number, imageUrl: string) => void;
  onAddLabelPair: (blockIndex: number) => void;
  onRemoveLabelPair: (blockIndex: number, pairIndex: number) => void;
  onLabelPairChange: (
    blockIndex: number,
    pairIndex: number,
    key: "label" | "text",
    value: string
  ) => void;
  onReferenceTypeChange: (
    blockIndex: number,
    referenceType: TaskEditorReferenceType
  ) => void;
  onReferencePresetTextChange: (blockIndex: number, text: string) => void;
  onAddTextInputField: (blockIndex: number) => void;
  onRemoveTextInputField: (blockIndex: number, fieldIndex: number) => void;
  onTextInputFieldChange: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onTextInputFieldOptionalToggle: (
    blockIndex: number,
    fieldIndex: number,
    optional: boolean
  ) => void;
  onImageFileChange: (blockIndex: number, file: File) => Promise<void>;
  onAddButtonOption: (blockIndex: number) => void;
  onRemoveButtonOption: (blockIndex: number, optionIndex: number) => void;
  onButtonOptionTextChange: (
    blockIndex: number,
    optionIndex: number,
    text: string
  ) => void;
  onButtonQuizOptionCorrectToggle: (
    blockIndex: number,
    optionIndex: number,
    isCorrect: boolean
  ) => void;
  // 분기 옵션의 "내부 블록 편집" 드릴다운 진입.
  onOpenBranchOption: (blockIndex: number, optionIndex: number) => void;
  onButtonDefaultAllowMultipleChange: (
    blockIndex: number,
    allow: boolean
  ) => void;
  onAudioFieldChange: (
    blockIndex: number,
    key: "artwork" | "audio_url" | "extra_audio_url",
    value: string
  ) => void;
  onAudioArtworkFileChange: (blockIndex: number, file: File) => Promise<void>;
  onTextEditBlur: () => void;
}

const BlockBody = React.memo(BlockBodyImpl, blockBodyPropsEqual);

function branchSegmentCountsEqual(
  prev: number[] | null,
  next: number[] | null
): boolean {
  if (prev === next) return true;
  if (!prev || !next || prev.length !== next.length) return false;
  return prev.every((value, i) => value === next[i]);
}

function blockBodyPropsEqual(
  prev: BlockBodyProps,
  next: BlockBodyProps
): boolean {
  // 핸들러 ref 는 비교 대상에서 제외 — 모두 useCallback([], blocksRef 기반) 으로 안정.
  return (
    prev.block === next.block &&
    prev.blockIndex === next.blockIndex &&
    prev.excludeBranchAction === next.excludeBranchAction &&
    branchSegmentCountsEqual(prev.branchSegmentCounts, next.branchSegmentCounts)
  );
}

function BlockBodyImpl(props: BlockBodyProps) {
  const { block, blockIndex, excludeBranchAction, onAddAction } = props;

  // 헤더 블록: 설명/유저 액션 섹션·type 셀렉터 없이 텍스트 입력만 노출.
  // JSON shape 은 동일 — description.content 만 편집.
  if (block.type === "header") {
    return (
      <TaskEditorTextField
        size="sm"
        sx={taskEditorTextareaFieldSx}
        multiline
        minRows={1}
        placeholder="헤더 텍스트를 입력하세요."
        value={block.description.content}
        data-testid={`task-editor-header-text-${blockIndex}`}
        onCommit={(next) =>
          props.onDescriptionContentChange(
            blockIndex,
            next,
            `text-content:${blockIndex}`
          )
        }
        onBlur={props.onTextEditBlur}
      />
    );
  }

  // 유저 액션 추가 메뉴 — type 을 바로 선택. (설명은 항상 존재해 추가 없음)
  // 분기 드릴다운 안에서는 분기형 제외 (깊이 1).
  const renderAddActionMenu = () => {
    if (block.action !== null) return null;
    return (
      <Dropdown>
        <MenuButton
          size="sm"
          variant="outlined"
          color="neutral"
          sx={{
            ...taskEditorSelectSx,
            minWidth: 0,
            px: 1.25,
          }}
          data-testid={`task-editor-add-component-action-${blockIndex}`}
        >
          + 추가
        </MenuButton>
        <Menu placement="bottom-start" sx={taskEditorSelectListboxSx}>
          {TASK_EDITOR_ACTION_COMPONENT_TYPES.filter(
            (type) => !(excludeBranchAction && type === "button_branch")
          ).map((type) => (
            <MenuItem
              key={type}
              data-testid={`task-editor-add-component-action-${blockIndex}-${type}`}
              onClick={() => onAddAction(blockIndex, type)}
            >
              {getTaskEditorComponentTypeLabel(type)}
            </MenuItem>
          ))}
        </Menu>
      </Dropdown>
    );
  };

  const renderSectionHeader = (section: "description" | "action") => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minHeight: "28px", mb: 0.75 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 700,
            fontSize: "12px",
            letterSpacing: "0.02em",
            lineHeight: 1.4,
          }}
        >
          {section === "description" ? "설명" : "유저 액션"}
        </Typography>
        {section === "description" ? (
          <Typography
            component="span"
            sx={{
              color: "#dc2626",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: 1.4,
            }}
          >
            *
          </Typography>
        ) : null}
      </Stack>
      {section === "action" ? renderAddActionMenu() : null}
    </Stack>
  );

  const renderDashedDivider = () => <Box sx={taskEditorDashedDividerSx} />;

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0}>
        {renderSectionHeader("description")}

        <Sheet
          sx={{
            ...taskEditorComponentCardSx,
            px: 1.25,
            py: 1.5,
            backgroundColor: "rgba(99, 102, 241, 0.05)",
            borderRadius: "8px",
          }}
          data-testid="task-editor-component-card"
          data-section="description"
        >
          <Stack spacing={1.25}>
            <DescriptionHeader
              blockIndex={blockIndex}
              descriptionType={block.description.type}
              onTypeChange={(nextType) =>
                props.onDescriptionTypeChange(blockIndex, nextType)
              }
            />

            <DescriptionEditor
              blockIndex={blockIndex}
              description={block.description}
              onContentChange={props.onDescriptionContentChange}
              onImageUrlChange={props.onDescriptionImageUrlChange}
              onAddLabelPair={props.onAddLabelPair}
              onRemoveLabelPair={props.onRemoveLabelPair}
              onLabelPairChange={props.onLabelPairChange}
              onReferenceTypeChange={props.onReferenceTypeChange}
              onReferencePresetTextChange={props.onReferencePresetTextChange}
              onImageFileChange={props.onImageFileChange}
              onTextEditBlur={props.onTextEditBlur}
            />
          </Stack>
        </Sheet>
      </Stack>

      {renderDashedDivider()}
      <Stack spacing={0}>
        {renderSectionHeader("action")}

        {block.action ? (
          <Sheet
            // description 카드와 동일한 spacing/정렬을 유지하되, 시각 분리를
            // 위해 옅은 배경 tint 와 둥근 모서리만 다시 살림.
            sx={{
              ...taskEditorComponentCardSx,
              px: 1,
              py: 0.75,
              backgroundColor: "rgba(100, 116, 139, 0.05)",
              borderRadius: "8px",
            }}
            data-testid="task-editor-component-card"
            data-section="action"
          >
            <Stack spacing={1.25}>
              <ActionComponentHeader
                blockIndex={blockIndex}
                actionType={block.action.type}
                excludeBranchAction={excludeBranchAction}
                onTypeChange={(nextType) =>
                  props.onActionComponentTypeChange(blockIndex, nextType)
                }
                onDelete={() => props.onRemoveAction(blockIndex)}
              />
              <ActionComponentEditor
                blockIndex={blockIndex}
                branchSegmentCounts={props.branchSegmentCounts}
                action={block.action}
                onAddTextInputField={props.onAddTextInputField}
                onRemoveTextInputField={props.onRemoveTextInputField}
                onTextInputFieldChange={props.onTextInputFieldChange}
                onTextInputFieldOptionalToggle={
                  props.onTextInputFieldOptionalToggle
                }
                onAddButtonOption={props.onAddButtonOption}
                onRemoveButtonOption={props.onRemoveButtonOption}
                onButtonOptionTextChange={props.onButtonOptionTextChange}
                onButtonQuizOptionCorrectToggle={
                  props.onButtonQuizOptionCorrectToggle
                }
                onOpenBranchOption={props.onOpenBranchOption}
                onButtonDefaultAllowMultipleChange={
                  props.onButtonDefaultAllowMultipleChange
                }
                onAudioFieldChange={props.onAudioFieldChange}
                onAudioArtworkFileChange={props.onAudioArtworkFileChange}
                onTextEditBlur={props.onTextEditBlur}
              />
            </Stack>
          </Sheet>
        ) : null}
      </Stack>
    </Stack>
  );
}

// ─── description 헤더 ────────────────────────────────────────────────────

interface DescriptionHeaderProps {
  blockIndex: number;
  descriptionType: TaskEditorDescriptionType;
  onTypeChange: (nextType: TaskEditorDescriptionType) => void;
}

function DescriptionHeader({
  blockIndex,
  descriptionType,
  onTypeChange,
}: DescriptionHeaderProps) {
  // 설명은 4개 type(기본/이미지/레이블/불러오기)을 바로 선택 — action 의
  // type 단일 selector 와 동일한 패턴. 필수라 삭제 버튼은 없다.
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Select<TaskEditorDescriptionType>
        size="sm"
        value={descriptionType}
        onChange={(_, value) => {
          if (!value || value === descriptionType) return;
          onTypeChange(value);
        }}
        sx={{
          ...taskEditorSelectSx,
          minWidth: 140,
        }}
        slotProps={{
          button: {
            "data-testid": `task-editor-description-type-select-${blockIndex}`,
            sx: taskEditorSelectButtonSx,
          },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        {TASK_EDITOR_DESCRIPTION_TYPES.map((type) => (
          <Option key={type} value={type}>
            {getTaskEditorDescriptionTypeLabel(type)}
          </Option>
        ))}
      </Select>
    </Stack>
  );
}

// ─── description 본문 ─────────────────────────────────────────────────────

interface DescriptionEditorProps {
  blockIndex: number;
  description: TaskEditorDescription;
  onContentChange: (
    blockIndex: number,
    content: string,
    groupKey: string
  ) => void;
  onImageUrlChange: (blockIndex: number, imageUrl: string) => void;
  onAddLabelPair: (blockIndex: number) => void;
  onRemoveLabelPair: (blockIndex: number, pairIndex: number) => void;
  onLabelPairChange: (
    blockIndex: number,
    pairIndex: number,
    key: "label" | "text",
    value: string
  ) => void;
  onReferenceTypeChange: (
    blockIndex: number,
    referenceType: TaskEditorReferenceType
  ) => void;
  onReferencePresetTextChange: (blockIndex: number, text: string) => void;
  onImageFileChange: (blockIndex: number, file: File) => Promise<void>;
  onTextEditBlur: () => void;
}

const DescriptionEditor = React.memo(
  DescriptionEditorImpl,
  descriptionEditorPropsEqual
);

function descriptionEditorPropsEqual(
  prev: DescriptionEditorProps,
  next: DescriptionEditorProps
): boolean {
  return (
    prev.description === next.description && prev.blockIndex === next.blockIndex
  );
}

// type 별 부가 필드 (basic 은 없음). 텍스트(content)는 공통이라 아래에서 항상 렌더.
function DescriptionExtraEditor({
  blockIndex,
  description,
  onImageUrlChange,
  onAddLabelPair,
  onRemoveLabelPair,
  onLabelPairChange,
  onReferenceTypeChange,
  onReferencePresetTextChange,
  onImageFileChange,
  onTextEditBlur,
}: Omit<DescriptionEditorProps, "onContentChange">) {
  switch (description.type) {
    case "basic":
      return null;

    case "image":
      return (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              component="label"
              size="sm"
              variant="outlined"
              color="neutral"
              sx={{
                ...taskEditorInlineAddButtonSx,
                width: "fit-content",
                flexShrink: 0,
              }}
            >
              파일 선택
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  const inputElement = event.target as HTMLInputElement;
                  const selectedFile = inputElement.files?.[0];

                  if (!selectedFile) {
                    return;
                  }

                  await onImageFileChange(blockIndex, selectedFile);
                  inputElement.value = "";
                }}
              />
            </Button>
            <Input
              size="sm"
              sx={{ flex: 1, ...taskEditorTextFieldSx }}
              placeholder={
                isTaskEditorLocalImageDataUrl(description.image_url)
                  ? "로컬 업로드 이미지"
                  : "이미지 URL (data: 또는 https://...)"
              }
              value={
                isTaskEditorLocalImageDataUrl(description.image_url)
                  ? "로컬에서 업로드한 이미지 (수정 불가)"
                  : description.image_url
              }
              readOnly={isTaskEditorLocalImageDataUrl(description.image_url)}
              disabled={isTaskEditorLocalImageDataUrl(description.image_url)}
              onChange={(event) => {
                if (isTaskEditorLocalImageDataUrl(description.image_url)) {
                  return;
                }
                onImageUrlChange(blockIndex, event.target.value);
              }}
              onBlur={onTextEditBlur}
            />
          </Stack>
          {description.image_url.trim() && (
            <Box sx={{ width: "100%" }}>
              <img
                src={description.image_url}
                alt="task-editor-image-preview"
                style={taskEditorImagePreviewSx as React.CSSProperties}
              />
            </Box>
          )}
        </Stack>
      );

    case "label": {
      // 레이블-텍스트 쌍 N개 — text_input 의 fields 처럼 최소 1개,
      // 1개일 때는 삭제 버튼 미노출. 카드/캡션 구조는 TextInputFieldsEditor 와 동일.
      const canRemovePair = description.pairs.length > 1;
      return (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          {description.pairs.map((pair, pairIndex) => (
            <Sheet
              key={`label-pair-${blockIndex}-${pairIndex}`}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid",
                borderColor: theme.vars.palette.Border.Weak,
                backgroundColor: theme.vars.palette.Surface.Forward,
                px: 1,
                py: 1,
              }}
            >
              <Stack spacing={0.75}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="Footnote"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Weak,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {`쌍 ${pairIndex + 1}`}
                  </Typography>
                  {canRemovePair ? (
                    <IconButton
                      size="sm"
                      aria-label="쌍 삭제"
                      data-testid={`task-editor-label-pair-remove-${blockIndex}-${pairIndex}`}
                      onClick={() => onRemoveLabelPair(blockIndex, pairIndex)}
                      sx={{ minHeight: "24px", minWidth: "24px" }}
                    >
                      <span
                        style={{ fontSize: "14px" }}
                        className="material-symbols-rounded"
                      >
                        close
                      </span>
                    </IconButton>
                  ) : null}
                </Stack>
                <Stack spacing={0.25}>
                  <Typography
                    variant="Footnote"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: theme.vars.palette.Content.Medium,
                    }}
                  >
                    레이블
                  </Typography>
                  <TaskEditorTextField
                    size="sm"
                    sx={{ ...taskEditorTextFieldSx, maxWidth: "240px" }}
                    placeholder="예: 생각"
                    value={pair.label}
                    data-testid={`task-editor-label-pair-label-${blockIndex}-${pairIndex}`}
                    onCommit={(next) =>
                      onLabelPairChange(blockIndex, pairIndex, "label", next)
                    }
                    onBlur={onTextEditBlur}
                  />
                </Stack>
                <Stack spacing={0.25}>
                  <Typography
                    variant="Footnote"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: theme.vars.palette.Content.Medium,
                    }}
                  >
                    텍스트
                  </Typography>
                  <TaskEditorTextField
                    size="sm"
                    sx={taskEditorTextFieldSx}
                    placeholder="예 이번 프로젝트는 나 때문에 다 망했어"
                    value={pair.text}
                    data-testid={`task-editor-label-pair-text-${blockIndex}-${pairIndex}`}
                    onCommit={(next) =>
                      onLabelPairChange(blockIndex, pairIndex, "text", next)
                    }
                    onBlur={onTextEditBlur}
                  />
                </Stack>
              </Stack>
            </Sheet>
          ))}
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            sx={{
              ...taskEditorInlineAddButtonSx,
              alignSelf: "flex-start",
            }}
            data-testid={`task-editor-label-pair-add-${blockIndex}`}
            onClick={() => onAddLabelPair(blockIndex)}
          >
            + 추가
          </Button>
        </Stack>
      );
    }

    case "reference":
      return (
        <Stack
          spacing={0.75}
          sx={{ minWidth: 0 }}
          data-testid={`task-editor-reference-editor-${blockIndex}`}
        >
          <Select<TaskEditorReferenceType>
            size="sm"
            value={description.reference_type}
            onChange={(_, value) => {
              if (!value || value === description.reference_type) return;
              onReferenceTypeChange(blockIndex, value);
            }}
            sx={{
              ...taskEditorSubtypeSelectSx,
              alignSelf: "flex-start",
              width: "fit-content",
              minWidth: 130,
            }}
            slotProps={{
              button: {
                "data-testid": `task-editor-reference-subtype-select-${blockIndex}`,
                sx: taskEditorSubtypeSelectButtonSx,
              },
              listbox: { sx: taskEditorSelectListboxSx },
            }}
          >
            {TASK_EDITOR_REFERENCE_TYPES.map((type) => (
              <Option key={type} value={type}>
                {getTaskEditorReferenceTypeLabel(type)}
              </Option>
            ))}
          </Select>
          {description.reference_type === "coach_preset" ? (
            <TaskEditorTextField
              size="sm"
              sx={taskEditorTextFieldSx}
              placeholder="플레이스홀더 (코치에게만 보임)"
              value={description.preset_text}
              data-testid={`task-editor-reference-preset-text-${blockIndex}`}
              onCommit={(next) => onReferencePresetTextChange(blockIndex, next)}
              onBlur={onTextEditBlur}
            />
          ) : (
            <Sheet
              variant="soft"
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: theme.vars.palette.Border.Weak,
                backgroundColor: theme.vars.palette.Background.Level1,
              }}
            >
              <Typography
                variant="Footnote"
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "12px",
                }}
              >
                유저의 자동적 사고 (저장된 데이터) 가 이 위치에 표시됩니다.
              </Typography>
            </Sheet>
          )}
        </Stack>
      );

    default: {
      const _never: never = description;
      void _never;
      return null;
    }
  }
}

function DescriptionEditorImpl(props: DescriptionEditorProps) {
  const { blockIndex, description, onContentChange, onTextEditBlur } = props;
  return (
    <Stack
      spacing={0.75}
      sx={{ minWidth: 0 }}
      data-testid="task-editor-element-card"
    >
      <TaskEditorTextField
        size="sm"
        sx={taskEditorTextareaFieldSx}
        multiline
        minRows={2}
        placeholder="내용을 입력하세요."
        value={description.content}
        onCommit={(next) =>
          onContentChange(blockIndex, next, `text-content:${blockIndex}`)
        }
        onBlur={onTextEditBlur}
      />
      <DescriptionExtraEditor {...props} />
    </Stack>
  );
}

// ─── action 헤더 ─────────────────────────────────────────────────────────

interface ActionComponentHeaderProps {
  blockIndex: number;
  actionType: TaskEditorActionComponentType;
  // 분기 드릴다운 스코프 안 — 타입 셀렉터에서 분기형 제외 (깊이 1)
  excludeBranchAction: boolean;
  onTypeChange: (nextType: TaskEditorActionComponentType) => void;
  onDelete: () => void;
}

function ActionComponentHeader({
  blockIndex,
  actionType,
  excludeBranchAction,
  onTypeChange,
  onDelete,
}: ActionComponentHeaderProps) {
  // 유저 액션은 4개 type(주관식/객관식/객관식 정답형/객관식 분기형)을 바로 선택.
  // 카테고리·버튼 sub-type 2단 구조를 없애고 type 단일 selector 로 일원화.
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
    >
      <Select<TaskEditorActionComponentType>
        size="sm"
        value={actionType}
        onChange={(_, value) => {
          if (!value || value === actionType) return;
          onTypeChange(value);
        }}
        sx={{
          ...taskEditorSelectSx,
          minWidth: 140,
        }}
        slotProps={{
          button: {
            "data-testid": `task-editor-action-type-select-${blockIndex}`,
            sx: taskEditorSelectButtonSx,
          },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        {TASK_EDITOR_ACTION_COMPONENT_TYPES.filter(
          (type) =>
            !(
              excludeBranchAction &&
              type === "button_branch" &&
              actionType !== "button_branch"
            )
        ).map((type) => (
          <Option key={type} value={type}>
            {getTaskEditorComponentTypeLabel(type)}
          </Option>
        ))}
      </Select>
      <IconButton
        size="sm"
        aria-label="컴포넌트 삭제"
        data-testid={`task-editor-delete-component-${blockIndex}-action`}
        onClick={onDelete}
      >
        <span style={{ fontSize: "16px" }} className="material-symbols-rounded">
          close
        </span>
      </IconButton>
    </Stack>
  );
}

// ─── action 본문 ─────────────────────────────────────────────────────────

interface ActionComponentEditorProps {
  blockIndex: number;
  // 루트 뷰의 분기 블록에만 — 옵션별 세그먼트(내부 블록) 수. 그 외 null.
  branchSegmentCounts: number[] | null;
  action: TaskEditorActionComponent;
  onAddTextInputField: (blockIndex: number) => void;
  onRemoveTextInputField: (blockIndex: number, fieldIndex: number) => void;
  onTextInputFieldChange: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onTextInputFieldOptionalToggle: (
    blockIndex: number,
    fieldIndex: number,
    optional: boolean
  ) => void;
  onAddButtonOption: (blockIndex: number) => void;
  onRemoveButtonOption: (blockIndex: number, optionIndex: number) => void;
  onButtonOptionTextChange: (
    blockIndex: number,
    optionIndex: number,
    text: string
  ) => void;
  onButtonQuizOptionCorrectToggle: (
    blockIndex: number,
    optionIndex: number,
    isCorrect: boolean
  ) => void;
  // 분기 옵션의 "내부 블록 편집" 드릴다운 진입.
  onOpenBranchOption: (blockIndex: number, optionIndex: number) => void;
  onButtonDefaultAllowMultipleChange: (
    blockIndex: number,
    allow: boolean
  ) => void;
  onAudioFieldChange: (
    blockIndex: number,
    key: "artwork" | "audio_url" | "extra_audio_url",
    value: string
  ) => void;
  onAudioArtworkFileChange: (blockIndex: number, file: File) => Promise<void>;
  onTextEditBlur: () => void;
}

const ActionComponentEditor = React.memo(
  ActionComponentEditorImpl,
  actionComponentEditorPropsEqual
);

function actionComponentEditorPropsEqual(
  prev: ActionComponentEditorProps,
  next: ActionComponentEditorProps
): boolean {
  return (
    prev.action === next.action &&
    prev.blockIndex === next.blockIndex &&
    branchSegmentCountsEqual(prev.branchSegmentCounts, next.branchSegmentCounts)
  );
}

function ActionComponentEditorImpl(props: ActionComponentEditorProps) {
  const {
    blockIndex,
    branchSegmentCounts,
    action,
    onAddTextInputField,
    onRemoveTextInputField,
    onTextInputFieldChange,
    onTextInputFieldOptionalToggle,
    onAddButtonOption,
    onRemoveButtonOption,
    onButtonOptionTextChange,
    onButtonQuizOptionCorrectToggle,
    onOpenBranchOption,
    onButtonDefaultAllowMultipleChange,
    onAudioFieldChange,
    onAudioArtworkFileChange,
    onTextEditBlur,
  } = props;

  if (action.type === "audio") {
    const isArtworkDataUrl = isTaskEditorLocalImageDataUrl(action.artwork);
    return (
      <Stack
        spacing={1.25}
        sx={{ minWidth: 0 }}
        data-testid="task-editor-audio-editor"
      >
        <Stack spacing={0.5}>
          <Typography variant="Footnote" sx={taskEditorCompactFieldLabelSx}>
            아트워크
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              component="label"
              size="sm"
              variant="outlined"
              color="neutral"
              sx={{
                ...taskEditorInlineAddButtonSx,
                width: "fit-content",
                flexShrink: 0,
              }}
            >
              파일 선택
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  const inputElement = event.target as HTMLInputElement;
                  const selectedFile = inputElement.files?.[0];
                  if (!selectedFile) return;
                  await onAudioArtworkFileChange(blockIndex, selectedFile);
                  inputElement.value = "";
                }}
              />
            </Button>
            <Input
              size="sm"
              sx={{ flex: 1, ...taskEditorTextFieldSx }}
              data-testid={`task-editor-audio-artwork-${blockIndex}`}
              placeholder={
                isArtworkDataUrl
                  ? "로컬 업로드 이미지"
                  : "이미지 URL (data: 또는 https://...)"
              }
              value={
                isArtworkDataUrl
                  ? "로컬에서 업로드한 이미지 (수정 불가)"
                  : action.artwork
              }
              readOnly={isArtworkDataUrl}
              disabled={isArtworkDataUrl}
              onChange={(event) => {
                if (isArtworkDataUrl) return;
                onAudioFieldChange(blockIndex, "artwork", event.target.value);
              }}
              onBlur={onTextEditBlur}
            />
          </Stack>
          {action.artwork.trim() && (
            <Box sx={{ width: "100%" }}>
              <img
                src={action.artwork}
                alt="task-editor-audio-artwork-preview"
                style={taskEditorImagePreviewSx as React.CSSProperties}
              />
            </Box>
          )}
        </Stack>

        <Stack spacing={0.25}>
          <Typography variant="Footnote" sx={taskEditorCompactFieldLabelSx}>
            오디오 URL
          </Typography>
          <TaskEditorTextField
            size="sm"
            sx={taskEditorTextFieldSx}
            placeholder="https://media.distancing.im/audio/....mp3"
            value={action.audio_url}
            data-testid={`task-editor-audio-url-${blockIndex}`}
            onCommit={(next) =>
              onAudioFieldChange(blockIndex, "audio_url", next)
            }
            onBlur={onTextEditBlur}
          />
        </Stack>

        <Stack spacing={0.25}>
          <Typography variant="Footnote" sx={taskEditorCompactFieldLabelSx}>
            추가 오디오 URL (선택)
          </Typography>
          <TaskEditorTextField
            size="sm"
            sx={taskEditorTextFieldSx}
            placeholder="본 오디오 종료 후 반복 재생 (선택)"
            value={action.extra_audio_url ?? ""}
            data-testid={`task-editor-audio-extra-url-${blockIndex}`}
            onCommit={(next) =>
              onAudioFieldChange(blockIndex, "extra_audio_url", next)
            }
            onBlur={onTextEditBlur}
          />
        </Stack>
      </Stack>
    );
  }

  if (action.type === "text_input") {
    return (
      <Stack
        spacing={0.5}
        sx={{ minWidth: 0 }}
        data-testid="task-editor-element-card"
      >
        <TextInputFieldsEditor
          blockIndex={blockIndex}
          fields={action.fields}
          onAddField={onAddTextInputField}
          onRemoveField={onRemoveTextInputField}
          onChangeField={onTextInputFieldChange}
          onToggleFieldOptional={onTextInputFieldOptionalToggle}
          onTextEditBlur={onTextEditBlur}
        />
      </Stack>
    );
  }

  // button_default / button_quiz / button_branch
  const renderOptionRow = (
    option: {
      text: string;
      is_correct?: boolean;
    },
    optionIndex: number
  ) => {
    return (
      <Stack
        key={`button-option-${blockIndex}-${optionIndex}`}
        spacing={0.5}
        sx={taskEditorButtonGroupOptionRowSx}
      >
        <Chip
          variant="soft"
          color="neutral"
          sx={taskEditorButtonGroupOptionChipSx}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {action.type === "button_quiz" ? (
              <Typography
                data-testid={`task-editor-quiz-option-label-${blockIndex}-${optionIndex}`}
                sx={{
                  flexShrink: 0,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                  userSelect: "none",
                }}
              >
                {`${getTaskEditorQuizOptionLabel(optionIndex)}.`}
              </Typography>
            ) : null}
            <TaskEditorTextField
              size="sm"
              variant="plain"
              sx={taskEditorButtonGroupOptionInputSx}
              placeholder={
                action.type === "button_quiz"
                  ? "옵션 내용"
                  : `옵션 ${optionIndex + 1}`
              }
              value={option.text}
              data-testid={`task-editor-button-option-input-${blockIndex}-${optionIndex}`}
              onCommit={(next) =>
                onButtonOptionTextChange(blockIndex, optionIndex, next)
              }
              onBlur={onTextEditBlur}
            />
            {action.type === "button_quiz" ? (
              <Checkbox
                size="sm"
                checked={option.is_correct === true}
                label="정답"
                data-testid={`task-editor-quiz-option-correct-${blockIndex}-${optionIndex}`}
                onChange={(event) =>
                  onButtonQuizOptionCorrectToggle(
                    blockIndex,
                    optionIndex,
                    event.target.checked
                  )
                }
              />
            ) : null}
            <IconButton
              size="sm"
              variant="plain"
              aria-label={`옵션 ${optionIndex + 1} 삭제`}
              data-testid={`task-editor-button-option-remove-${blockIndex}-${optionIndex}`}
              onClick={() => onRemoveButtonOption(blockIndex, optionIndex)}
              sx={{
                minWidth: "24px",
                minHeight: "24px",
                borderRadius: "999px",
              }}
            >
              <span
                style={{ fontSize: "14px" }}
                className="material-symbols-rounded"
              >
                close
              </span>
            </IconButton>
          </Stack>
        </Chip>

        {action.type === "button_branch" && branchSegmentCounts ? (
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            data-testid={`task-editor-branch-option-edit-${blockIndex}-${optionIndex}`}
            onClick={() => onOpenBranchOption(blockIndex, optionIndex)}
            sx={taskEditorInlineAddButtonSx}
          >
            {`내부 블록 편집 (${branchSegmentCounts[optionIndex] ?? 0}개)`}
          </Button>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box
      sx={taskEditorButtonGroupEditorSx}
      data-testid={
        action.type === "button_quiz"
          ? "task-editor-quiz-editor"
          : action.type === "button_default"
            ? "task-editor-button-selection-editor"
            : "task-editor-button-group-editor"
      }
    >
      <Stack spacing={0.5} sx={taskEditorCompactFieldSx}>
        <Box sx={taskEditorButtonGroupGuideTriggerSx}>
          <Typography variant="Footnote" sx={taskEditorCompactFieldLabelSx}>
            옵션
          </Typography>
          {action.type === "button_branch" ? (
            <Tooltip
              arrow
              placement="top-start"
              sx={taskEditorButtonGroupGuideTooltipSlotSx}
              slotProps={{
                arrow: { sx: taskEditorButtonGroupGuideTooltipArrowSx },
              }}
              title={
                <Typography
                  component="div"
                  sx={taskEditorButtonGroupGuideTooltipSx}
                >
                  {TASK_EDITOR_BUTTON_BRANCH_GUIDE_TEXT}
                </Typography>
              }
            >
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                aria-label="옵션 분기 가이드"
                sx={taskEditorButtonGroupGuideIconSx}
              >
                <span
                  style={{ fontSize: "14px" }}
                  className="material-symbols-rounded"
                >
                  help
                </span>
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box
          sx={{
            ...taskEditorButtonGroupOptionListSx,
            // 분기형은 옵션마다 "내부 블록 편집" 버튼이 따라붙는다 — 옵션
            // 사이는 넓게 띄우고, 같은 옵션의 chip+편집 버튼은 붙여서
            // 덩어리(옵션 단위)가 명확히 구분되게 한다.
            ...(action.type === "button_branch" ? { gap: 2.5 } : {}),
          }}
        >
          {action.options.map((option, optionIndex) =>
            renderOptionRow(option, optionIndex)
          )}

          {action.type === "button_default" ? (
            <Stack spacing={0.5}>
              <Checkbox
                size="sm"
                checked={action.allow_multiple_selection}
                label="복수 선택 허용"
                data-testid={`task-editor-button-default-allow-multiple-${blockIndex}`}
                onChange={(event) =>
                  onButtonDefaultAllowMultipleChange(
                    blockIndex,
                    event.target.checked
                  )
                }
              />
            </Stack>
          ) : null}

          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            data-testid={`task-editor-button-add-option-${blockIndex}`}
            onClick={() => onAddButtonOption(blockIndex)}
            sx={taskEditorInlineAddButtonSx}
          >
            + 옵션 추가
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ─── 텍스트 입력 필드 편집기 ──────────────────────────────────────────────

interface TextInputFieldsEditorProps {
  blockIndex: number;
  fields: {
    label?: string;
    placeholder?: string;
    help_text?: string;
    optional?: boolean;
  }[];
  onAddField: (blockIndex: number) => void;
  onRemoveField: (blockIndex: number, fieldIndex: number) => void;
  onChangeField: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onToggleFieldOptional: (
    blockIndex: number,
    fieldIndex: number,
    optional: boolean
  ) => void;
  onTextEditBlur: () => void;
}

const textInputFieldRows: {
  key: "label" | "placeholder" | "help_text";
  label: string;
  placeholder: string;
}[] = [
  { key: "label", label: "라벨", placeholder: "예: 오늘의 소감" },
  {
    key: "placeholder",
    label: "플레이스홀더",
    placeholder: "예: 소감을 남겨보세요",
  },
  {
    key: "help_text",
    label: "도움말",
    placeholder: "예: 자유롭게 적어도 됩니다",
  },
];

function TextInputFieldsEditor({
  blockIndex,
  fields,
  onAddField,
  onRemoveField,
  onChangeField,
  onToggleFieldOptional,
  onTextEditBlur,
}: TextInputFieldsEditorProps) {
  const canRemove = fields.length > 1;

  return (
    <Stack spacing={1}>
      {fields.map((field, fieldIndex) => (
        <Sheet
          key={`text-input-field-${blockIndex}-${fieldIndex}`}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            border: "1px solid",
            borderColor: theme.vars.palette.Border.Weak,
            backgroundColor: theme.vars.palette.Surface.Forward,
            px: 1,
            py: 1,
          }}
          data-testid={`task-editor-text-input-field-${blockIndex}-${fieldIndex}`}
        >
          <Stack spacing={0.75}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="Footnote"
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Weak,
                  letterSpacing: "0.02em",
                }}
              >
                {`인풋 ${fieldIndex + 1}`}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="sm"
                  label="선택 입력"
                  checked={field.optional === true}
                  data-testid={`task-editor-text-input-field-optional-${blockIndex}-${fieldIndex}`}
                  onChange={(event) =>
                    onToggleFieldOptional(
                      blockIndex,
                      fieldIndex,
                      event.target.checked
                    )
                  }
                  sx={{ fontSize: "11px" }}
                />
                {canRemove ? (
                  <IconButton
                    size="sm"
                    aria-label="인풋 삭제"
                    data-testid={`task-editor-remove-text-input-field-${blockIndex}-${fieldIndex}`}
                    onClick={() => onRemoveField(blockIndex, fieldIndex)}
                    sx={{ minHeight: "24px", minWidth: "24px" }}
                  >
                    <span
                      style={{ fontSize: "14px" }}
                      className="material-symbols-rounded"
                    >
                      close
                    </span>
                  </IconButton>
                ) : null}
              </Stack>
            </Stack>
            {textInputFieldRows.map((row) => (
              <Stack key={row.key} spacing={0.25}>
                <Typography
                  variant="Footnote"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: theme.vars.palette.Content.Medium,
                  }}
                >
                  {row.label}
                </Typography>
                <TaskEditorTextField
                  size="sm"
                  sx={taskEditorTextFieldSx}
                  placeholder={row.placeholder}
                  value={field[row.key] ?? ""}
                  data-testid={`task-editor-text-input-field-input-${blockIndex}-${fieldIndex}-${row.key}`}
                  onCommit={(next) =>
                    onChangeField(blockIndex, fieldIndex, row.key, next)
                  }
                  onBlur={onTextEditBlur}
                />
              </Stack>
            ))}
          </Stack>
        </Sheet>
      ))}
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        sx={{
          ...taskEditorInlineAddButtonSx,
          alignSelf: "flex-start",
        }}
        data-testid={`task-editor-add-text-input-field-${blockIndex}`}
        onClick={() => onAddField(blockIndex)}
      >
        + 인풋 추가
      </Button>
    </Stack>
  );
}

// ─── 블록 JSON 보기 모달 ──────────────────────────────────────────────────

interface TaskEditorBlockJsonModalProps {
  target: { block: TaskEditorBlock; label: string } | null;
  onClose: () => void;
}

function TaskEditorBlockJsonModal({
  target,
  onClose,
}: TaskEditorBlockJsonModalProps) {
  const json = target ? orderedStringify(target.block) : "";
  return (
    <Modal open={Boolean(target)} onClose={onClose}>
      <ModalDialog
        size="md"
        sx={{
          maxWidth: 720,
          width: "92vw",
          maxHeight: "90vh",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModalClose />
        <Typography
          level="title-lg"
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            color: theme.vars.palette.Content.Strong,
            mb: 0.25,
          }}
        >
          {target ? target.label : "블록"}
        </Typography>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Weak,
            fontSize: "12px",
            mb: 1.5,
          }}
        >
          개별 블록의 JSON 객체입니다.
        </Typography>
        <Box
          data-testid="task-editor-block-json"
          sx={{ flex: 1, minHeight: 0, display: "flex" }}
        >
          <JsonCodeBox json={json} />
        </Box>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 1.5 }}
        >
          <Button size="sm" variant="Plain" color="primary" onClick={onClose}>
            확인
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
